import React, { useEffect, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useMutation } from "urql";
import { addNoScroll, getString, handlePostRequest, login, removeNoScroll } from "../../utilities";
import { useLazyQuery } from "../../hooks";
import { RECAPTCHA_VERIFY_QUERY } from "../../graphql/queries";
import { MANAGE_ACCOUNT_MUTATION } from "../../graphql/mutations";
import { setShowAccountComplete } from "../../redux/slices/accountCompleteSlice";
import { emailSelector, roleSelector } from "../../redux/slices/loginSlice";
import {
  ACCESS_NAME_CHAR_LIMIT,
  EMAIL_CHAR_LIMIT,
  getLangCode,
  language,
  PASSWORD_CHAR_LIMIT,
  PASSWORD_CHAR_MINIMUM,
  recaptchaSiteKey,
  userRoles,
} from "../../config";
import welcomeAnimationPath from "../../../assets/lottie/welcomeAnimation.json";
import LazyLottieAnimation from "./LazyLottieAnimation";
import LoadingAnim from "./loadingAnim";
import ErrorMessage from "./errorMessage";
import TextViewHook from "./textViewHook";
import {
  getMaxLengthValidator,
  getMinLengthValidator,
  validateAlphaNumeric,
  validateEmail,
} from "../../utilities/forms/rules";
import ModalClose from "./modalClose";
import { useLocation, useNavigate } from "react-router";
import {
  getErrorMessageText,
  inferSubmissionFailureReasonAndType,
  mpAccountCreationFailed,
  mpCreateAccountCTASelected,
  mpFieldValidationFailed,
  mpModalClicked,
  mpModalShown,
  mpAccountCreationSucceeded,
  inferInlineValidationErrorType,
} from "../../utilities/mixpanel";
import { routeCodes } from "../../config/routes";
import { getItem } from "../../utilities/localStorage";

export default function AccountComplete() {
  const action = `Account_Complete_Form_Submission`;

  const dispatch = useDispatch();
  const langCode = getLangCode();

  const signupEmail = useSelector(emailSelector);
  const role = useSelector(roleSelector);

  const [submitting, setSubmitting] = useState(false);
  const [submitErrors, setSubmitErrors] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const startTime = useRef(new Date());

  const hasStudentAccess = [userRoles.classroom, userRoles.school].includes(role);

  const getTriggerType = () => {
    const pageviewCount = window?.sessionStorage?.getItem("pageviewcount");
    const onManagePage = location.pathname.includes(routeCodes.MANAGE.slice(0, -1));
    const isInitialPageLoad = pageviewCount === null || parseInt(pageviewCount, 10) <= 1;

    if (onManagePage) {
      return "manage_account";
    }

    return isInitialPageLoad ? "initial_page_load" : "successful_subscription";
  };

  const modalEventData = {
    modalType: "full_screen_account_setup",
    header: getString("accountComplete.title"),
    primaryCta: getString(
      hasStudentAccess ? "accountComplete.form.cta.classSchool" : "accountComplete.form.cta.family",
    ),
    secondaryCta: "none",
    tertiaryCta: "none",
    isDismissible: true,
    pathname: location.pathname,
  };

  const methods = useForm({
    mode: "onBlur",
    reValidateMode: "onChange",
    defaultValues: {
      email: signupEmail,
      password: "",
      ...(!!hasStudentAccess && { accessName: "", accessCode: "" }),
    },
  });

  const {
    formState: { errors, isSubmitted },
    getFieldState,
    trigger,
    handleSubmit,
    getValues,
  } = methods;

  useEffect(() => {
    addNoScroll();

    return () => {
      removeNoScroll();
    };
  }, []);

  const onError = (error) => {
    setSubmitting(false);
    setSubmitErrors(error);
    // Send Account Creation Failed event to Mixpanel
    const messageDisplayed = getErrorMessageText(error);
    const { errorType } = inferSubmissionFailureReasonAndType(messageDisplayed);
    mpAccountCreationFailed({
      planGroup: role,
      subMonths: getItem("subMonths"),
      modalTriggerType: getTriggerType(),
      errors: [
        {
          // eslint-disable-next-line camelcase
          error_type: errorType,
          // eslint-disable-next-line camelcase
          message_displayed: messageDisplayed,
        },
      ],
    });
  };

  const onChange = (inputName) => {
    if (errors[inputName]) {
      trigger(inputName);
    }

    if (submitErrors) {
      setSubmitErrors(null);
    }
  };

  const onUpdateCompleted = (data) => {
    setSubmitting(false);
    // Send Account Creation Succeeded event to Mixpanel
    mpAccountCreationSucceeded({
      planGroup: role,
      subMonths: getItem("subMonths"),
      modalTriggerType: getTriggerType(),
    });
    login({ token: data.updateUser.token });
  };

  const [result, updateUser] = useMutation(MANAGE_ACCOUNT_MUTATION);
  const { fetching: updateUserLoading } = result;

  const onRecaptchaCompleted = (data) => {
    const { reCaptchaVerify } = data;

    if (reCaptchaVerify.success) {
      const { email, password } = getValues();
      const variables = { password, email };

      if (hasStudentAccess) {
        variables.accessCode = getValues("accessCode");
        variables.accessName = getValues("accessName");
      }
      updateUser(variables).then((result) => handlePostRequest(result, onUpdateCompleted, onError));
    } else {
      onError({ message: reCaptchaVerify.message });
    }
  };

  const { executeQuery: recaptchaVerify, loading: recaptchaLoading } = useLazyQuery(
    RECAPTCHA_VERIFY_QUERY,
    onRecaptchaCompleted,
    onError,
  );

  const isLoading = submitting || recaptchaLoading || updateUserLoading;

  const onSubmit = () => {
    setSubmitting(true);
    window.grecaptcha.enterprise.ready(async () => {
      const token = await window.grecaptcha.enterprise.execute(recaptchaSiteKey, { action });

      if (token) {
        recaptchaVerify({ token, action, verify: false, siteKey: recaptchaSiteKey });
      }
    });
  };

  const onFormError = (formErrors) => {
    const formFieldErrorTypeMapping = {
      email: { required: "empty_email", invalid: "invalid_email_format" },
      password: { required: "empty_password", invalid: "invalid_password" },
      accessName: { required: "empty_access_name", invalid: "invalid_access_name" },
      accessCode: { required: "empty_access_code", invalid: "invalid_access_code" },
    };

    const errors = Object.entries(formFieldErrorTypeMapping).flatMap(([fieldName, errorTypeMapping]) => {
      const fieldError = formErrors?.[fieldName];

      if (!fieldError) {
        return [];
      }

      return [
        {
          // eslint-disable-next-line camelcase
          error_type: fieldError.type === "required" ? errorTypeMapping.required : errorTypeMapping.invalid,
          // eslint-disable-next-line camelcase
          message_displayed: fieldError.message || "",
        },
      ];
    });

    mpAccountCreationFailed({
      planGroup: role,
      subMonths: getItem("subMonths"),
      modalTriggerType: getTriggerType(),
      errors,
    });
  };

  const trackFieldValidationFailed = ({ fieldName = "", errorMessageDisplayed = "" }) => {
    if (!fieldName || !errorMessageDisplayed) {
      return;
    }

    mpFieldValidationFailed({
      fieldName,
      errorType: inferInlineValidationErrorType(fieldName),
      errorMessageDisplayed,
      form: "account_setup_form",
    });
  };

  const handleFieldBlurValidation = async (fieldName) => {
    const isValid = await trigger(fieldName);
    if (!isValid) {
      const fieldError = getFieldState(fieldName).error;
      if (fieldError?.type !== "required" || isSubmitted) {
        trackFieldValidationFailed({ fieldName, errorMessageDisplayed: fieldError?.message || "" });
      }
    }
  };

  const getErrors = (name) => {
    if (!isSubmitted && errors[name]?.type === "required") {
      return {};
    }

    return errors;
  };

  useEffect(() => {
    // send Modal Shown event to Mixpanel
    try {
      const triggerType = getTriggerType();
      mpModalShown({
        ...modalEventData,
        triggerType,
      });
    } catch (e) {}
  }, []);

  return (
    <>
      <div className="account-complete-wrapper">
        <div className="account-complete-container">
          <h1>{getString("accountComplete.title")}</h1>
          <h2>{getString("accountComplete.subTitle")}</h2>
          <div className="account-complete-animation">
            <LazyLottieAnimation animationPath={welcomeAnimationPath} fallback={<LoadingAnim className="orange" />} />
          </div>
          <h2>{getString(`accountComplete.sectionTitle.${hasStudentAccess ? "classSchool" : "family"}`)}</h2>

          <form className="account-complete-form" onSubmit={handleSubmit(onSubmit, onFormError)} autoComplete="off">
            <FormProvider {...methods}>
              <div className="account-complete-form-container">
                {hasStudentAccess && (
                  <>
                    <h3>{getString("accountComplete.form.classSchool.title")}</h3>
                    <div className="account-complete-form-caption">
                      {getString(`accountComplete.form.classSchool.text`, {
                        replace: [getString(`${role === userRoles.school ? "school.0" : "classroom"}`).toLowerCase()],
                      })}
                    </div>
                  </>
                )}

                <TextViewHook
                  rules={{
                    required: getString(
                      langCode === language.la
                        ? "forms.errorMessages.isRequired.1"
                        : "forms.errorMessages.isRequired.0",
                      {
                        replace: [getString(langCode === language.la ? "forms.email.8" : "forms.email.2")],
                      },
                    ),
                    validate: (value) =>
                      !value ||
                      validateEmail(value, getString(langCode === language.la ? "forms.email.6" : "forms.email.0")),
                    maxLength: getMaxLengthValidator(
                      getString(langCode === language.la ? "forms.email.6" : "forms.email.0"),
                      EMAIL_CHAR_LIMIT,
                    ),
                    onBlur: async () => {
                      await handleFieldBlurValidation("email");
                    },
                  }}
                  errors={getErrors("email")}
                  className="col1"
                  name="email"
                  placeholder={getString(langCode === language.la ? "forms.email.2" : "forms.email.0")}
                  label={getString(langCode === language.la ? "forms.email.2" : "forms.email.0")}
                  shouldLabelAnimate={false}
                  onChange={() => {
                    onChange("email");
                  }}
                />

                <TextViewHook
                  rules={{
                    required: getString(
                      langCode === language.la
                        ? "forms.errorMessages.isRequired.1"
                        : "forms.errorMessages.isRequired.0",
                      { replace: [getString(langCode === language.la ? "forms.password.3" : "forms.password.0")] },
                    ),
                    maxLength: getMaxLengthValidator(
                      getString(langCode === language.la ? "forms.password.4" : "forms.password.0"),
                      PASSWORD_CHAR_LIMIT,
                    ),
                    minLength: getMinLengthValidator(
                      getString(langCode === language.la ? "forms.password.4" : "forms.password.0"),
                      PASSWORD_CHAR_MINIMUM,
                    ),
                    onBlur: async () => {
                      await handleFieldBlurValidation("password");
                    },
                  }}
                  errors={getErrors("password")}
                  className="col1"
                  name="password"
                  type="password"
                  placeholder={getString("forms.password.2")}
                  label={getString("forms.password.0")}
                  shouldPasswordToggle={true}
                  shouldLabelAnimate={false}
                  onChange={() => {
                    onChange("password");
                  }}
                  autoComplete={"new-password"}
                />

                {hasStudentAccess && (
                  <>
                    <h3>{getString("accountComplete.form.classSchool.student.title")}</h3>
                    <div className="account-complete-form-caption">
                      {getString(`accountComplete.form.classSchool.student.text`)}
                    </div>

                    <TextViewHook
                      rules={{
                        required: getString("forms.errorMessages.isRequired.0", {
                          replace: [getString("forms.accessName")],
                        }),
                        validate: {
                          lowAndNo: (value) => validateAlphaNumeric(getString("forms.accessName"), value),
                        },
                        maxLength: getMaxLengthValidator(getString("forms.accessName"), ACCESS_NAME_CHAR_LIMIT),
                        onBlur: async () => {
                          await handleFieldBlurValidation("accessName");
                        },
                      }}
                      errors={getErrors("accessName")}
                      className="col1"
                      name="accessName"
                      placeholder={getString("accountComplete.form.classSchool.student.namePlaceholder")}
                      label={getString("forms.accessName")}
                      shouldLabelAnimate={false}
                      onChange={() => {
                        onChange("accessName");
                      }}
                    />

                    <TextViewHook
                      rules={{
                        required: getString("forms.errorMessages.isRequired.0", {
                          replace: [getString("forms.accessCode")],
                        }),
                        maxLength: getMaxLengthValidator(getString("forms.accessCode"), PASSWORD_CHAR_LIMIT),
                        onBlur: async () => {
                          await handleFieldBlurValidation("accessCode");
                        },
                      }}
                      errors={getErrors("accessCode")}
                      className="col1"
                      name="accessCode"
                      placeholder={getString("accountComplete.form.classSchool.student.codePlaceholder")}
                      label={getString("forms.accessCode")}
                      shouldLabelAnimate={false}
                      onChange={() => {
                        onChange("accessCode");
                      }}
                    />
                  </>
                )}
                {!!submitErrors && <ErrorMessage error={submitErrors} />}

                <button
                  type="submit"
                  className="button-flat-color pt-green round auto"
                  disabled={isLoading}
                  onClick={() => {
                    const elapsed = Math.floor((new Date() - startTime.current) / 1000);
                    mpModalClicked({
                      ...modalEventData,
                      triggerType: getTriggerType(),
                      interaction: "primary cta",
                    });
                    mpCreateAccountCTASelected({
                      planGroup: role,
                      subMonths: getItem("subMonths"),
                      timeOnPageSeconds: elapsed,
                      modalTriggerType: getTriggerType(),
                    });
                  }}
                >
                  {getString(
                    hasStudentAccess ? "accountComplete.form.cta.classSchool" : "accountComplete.form.cta.family",
                  )}
                </button>
              </div>
            </FormProvider>
          </form>

          <div />
        </div>

        {!isLoading && (
          <ModalClose
            close={() => {
              mpModalClicked({
                ...modalEventData,
                triggerType: getTriggerType(),
                interaction: "dismissed",
              });

              dispatch(
                setShowAccountComplete({
                  showAccountComplete: false,
                }),
              );

              if (location.pathname.includes(routeCodes.SUBSCRIBE)) {
                navigate(routeCodes.HOME, { replace: true });
              }
            }}
          />
        )}
      </div>
      {isLoading && <LoadingAnim position="fixed" className="background-transparent-pt-dark-space orange" />}
    </>
  );
}
